/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './ActionIconsColumn';

const ActionIconsColumnManager: ComponentManagerFn<'ActionIconsColumn'> = (
  platform: Platform,
): ComponentManager<'ActionIconsColumn'> => {
  return {
    ...baseComponentManager<'ActionIconsColumn'>(platform, 'ActionIconsColumn'),
    type: 'ActionIconsColumn',
    name: 'Action Icons Column',
    isChildAllowed: (page, item, childType) =>
      (['Icon'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
      },
      showOnlyOnHover: {
        label: 'Show Only On Hover',
        type: 'boolean',
      },
    },
    defaultChildren: ['Icon', 'Icon', 'Icon'],
    isPropertyVisible(prop, _, { itemId, appUid, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'viewAttribute':
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'sortable':
        case 'required':
        case 'helperText':
        case 'colorExpression':
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'play' },
    description: `**Overview**<br/>
The Action Icons Column is a specialized container column designed to hold interactive buttons for row-level operations.
Unlike standard data columns that display values (like text or numbers), this column repeats a set of Icon components for every row in the grid. It is the standard way to implement "Actions" such as Edit, Delete, View Details, or Download directly within the grid interface.

**1. Composition & Content**<br/>
This column acts purely as a layout container. It does not render data itself; instead, it renders its child components.
* **Allowed Children:** You can only add Icon components inside this column.
* **Default State:** When added, it automatically populates with 3 placeholder icons to help you visualize the layout immediately. You can remove or add icons as needed.

**2. Visual Behavior**<br/>
To keep your grid looking clean and uncluttered, you can control when these actions are visible.
* **Show Only On Hover:**
    * **Enabled (True):** The icons remain hidden until the user moves their mouse over a specific row. This creates a cleaner "Read Mode" interface.
    * **Disabled (False):** The icons are always visible in every row. Best for mobile interfaces or workflows where actions must be obvious.

**3. Interactivity (Making it Work)**<br/>
The column itself doesn't trigger logic; the icons inside it do.
* **How to Add Logic:**
    1. Select one of the **Icon** components inside the column (e.g., the "Trash Can").
    2. Add a **TriggerOnClick** child to that Icon.
    3. Bind the trigger to an action (e.g., Delete Record).
* **Context Awareness:** Because the icon lives inside a specific row, the trigger automatically knows which record was clicked. You can access the row's data (e.g., \`$._.id\`) inside your action logic.

**4. Restrictions**<br/>
Because this column is for actions, not data values, standard column features are disabled:
* **No Sorting/Filtering:** You cannot sort or filter rows based on the "Edit" button.
* **No View Attribute:** It does not bind to a specific field in the Data Store.`,
  };
};

export default ActionIconsColumnManager;
